/**
 * Server Implementation
 *
 * Orchestrates transport + pipelines + subscriptions for a single server connection.
 */

import type { IServer, IServerRegistration, ITransport, IPipeline } from '../types';
import type { TransportStatus, IEnvelope } from '../types';
import { SubscriptionManager } from '../subscriptions/SubscriptionManager';
import { IncomingPipeline } from '../pipeline/IncomingPipeline';
import { OutgoingPipeline } from '../pipeline/OutgoingPipeline';
import { PipelineContext } from '../pipeline/PipelineContext';
import { getDefaultRegistry } from '../protocols/ProtocolParserRegistry';
import { createModuleLogger } from '../utils';
import { createGuid } from '../utils/idGenerator';
import { LoggingPipelineObserver } from '../observability/LoggingPipelineObserver';

export class Server implements IServer {
  readonly id: string;

  readonly type: any;

  readonly transport: ITransport;

  readonly incomingPipeline: IPipeline;

  readonly outgoingPipeline: IPipeline;

  readonly subscriptionManager: SubscriptionManager;

  private logger = createModuleLogger('Server');

  private isRunning = false;

  private unsubscribeHandlers: Array<() => void> = [];

  constructor(registration: IServerRegistration, transport: ITransport) {
    this.id = registration.id;
    this.type = registration.type;
    this.transport = transport;
    this.subscriptionManager = new SubscriptionManager();
    this.incomingPipeline = new IncomingPipeline();
    this.outgoingPipeline = new OutgoingPipeline();

    // Wire observability logging
    const observer = new LoggingPipelineObserver();
    (this.incomingPipeline as IncomingPipeline).addObserver(observer);
    (this.outgoingPipeline as OutgoingPipeline).addObserver(observer);
    this.subscriptionManager.setObserver(observer);
  }

  get status(): TransportStatus {
    return this.transport.status;
  }

  async start(): Promise<void> {
    if (this.isRunning) {
      return;
    }

    this.logger.info(`Starting server ${this.id}`);

    try {
      // Setup message handler
      const unsubMsg = this.transport.onMessage((raw: string | ArrayBuffer) => {
        this.handleIncomingMessage(raw);
      });
      this.unsubscribeHandlers.push(unsubMsg);

      await this.transport.connect();
      this.isRunning = true;
    } catch (error) {
      this.logger.error(`Failed to start server ${this.id}`, error as Error);
      throw error;
    }
  }

  async stop(): Promise<void> {
    if (!this.isRunning) {
      return;
    }

    this.logger.info(`Stopping server ${this.id}`);

    try {
      for (const unsub of this.unsubscribeHandlers) {
        unsub();
      }
      this.unsubscribeHandlers = [];

      await this.transport.disconnect();
      this.subscriptionManager.clear();
      this.isRunning = false;
    } catch (error) {
      this.logger.error(`Error stopping server ${this.id}`, error as Error);
    }
  }

  async send(topic: string, body: unknown): Promise<void> {
    // Create envelope for outgoing message
    const envelope: IEnvelope = {
      meta: {
        id: createGuid(),
        serverId: this.id,
        transport: this.transport.type,
        protocol: 'json',
        receivedAt: new Date(),
        topic,
      },
      direction: 'outgoing',
      raw: JSON.stringify(body),
      body,
    };

    // Execute outgoing pipeline
    const context = new PipelineContext(envelope, this.id);
    const result = await this.outgoingPipeline.execute(context, envelope.meta.id);

    if (!result.success) {
      throw new Error(`Outgoing pipeline failed: ${result.error}`);
    }

    // Send via transport
    await this.transport.send(context.body as string | ArrayBuffer);
  }

  private async handleIncomingMessage(raw: string | ArrayBuffer): Promise<void> {
    try {
      // Parse message
      const registry = getDefaultRegistry();
      const parser = registry.resolve(raw);
      const parsed = parser.parse(raw);

      if (parsed.error) {
        this.logger.warn(`Parse error: ${parsed.error}`);
        return;
      }

      // Create envelope
      const envelope: IEnvelope = {
        meta: {
          id: createGuid(),
          serverId: this.id,
          transport: this.transport.type,
          protocol: (parsed.format as any) || 'json',
          receivedAt: new Date(),
        },
        direction: 'incoming',
        raw,
        body: parsed.body,
      };

      // Execute incoming pipeline
      const context = new PipelineContext(envelope, this.id);
      await this.incomingPipeline.execute(context, envelope.meta.id);
    } catch (error) {
      this.logger.error('Message handling error', error as Error);
    }
  }
}
